import React, { lazy, Suspense, useEffect } from "react";
import { Redirect, Route, Switch, useHistory, useLocation } from "react-router-dom";
import { Loading } from "podro-design-system";

import CityDataProvider from "./utils/storages/cityDataProvider";
import OrderDataProvider from "./utils/storages/orderDataProvider";
import BoxSizeDataProvider from "./utils/storages/boxSizeDataProvider";
import OrderFiltersDataProvider from "./utils/storages/orderFiltersDataProvider";
import BulkDataProvider from "./utils/storages/bulkDataProvider";

const PrivacyPolicyFa = lazy(() => import("./pages/privacyPolicy/PrivacyPolicyFa"));
const ReceiverTracking = lazy(() => import("./pages/receiverTracking/ReceiverTracking"));
const ReceiverFeedback = lazy(() => import("./pages/receiverFeedback/ReceiverFeedback"));
const SenderFeedback = lazy(() => import("./pages/senderFeedback/SenderFeedback"));
const Login = lazy(() => import("./pages/login/Login"));
const Register = lazy(() => import("./pages/register/Register"));
const VerifyCode = lazy(() => import("./pages/verifyCode/VerifyCode"));
const NotFound = lazy(() => import("./pages/notFound/NotFound"));

const Profile = lazy(() => import("./pages/profile/Profile"));
const ProfileInfo = lazy(() => import("./pages/profileInfo/ProfileInfo"));
const AddressBook = lazy(() => import("./pages/addressBook/AddressBook"));
const Dashboard = lazy(() => import("./pages/dashboard/dashboard"));

const SingleOrder = lazy(() => import("./pages/singleOrder/SingleOrder"));
const BulkOrder = lazy(() => import("./pages/bulkOrder/BulkOrder"));

const Cart = lazy(() => import("./pages/cart/Cart"));
const Shipping = lazy(() => import("./pages/shipping/Shipping"));
const ShippingInfo = lazy(() => import("./pages/shippingInfo/ShippingInfo"));
const Payment = lazy(() => import("./pages/payment/Payment"));
const Finalized = lazy(() => import("./pages/Finalized/Finalized"));

const OrdersList = lazy(() => import("./pages/ordersList/OrdersList"));
const OrderDetails = lazy(() => import("./pages/orderDetails/OrderDetails"));
const FileList = lazy(() => import("./pages/fileList/FileList"));

const AuthContainer = lazy(() => import("./commonComponents/authContainer/AuthContainer"));
const NewOrderContainer = lazy(() => import("./commonComponents/newOrderContainer/NewOrderContainer"));
const PageView = lazy(() => import("./commonComponents/pageView/PageView"));


const Router = () => {
  const { pathname } = useLocation();
  const history = useHistory();

  const isLoggedIn = () => JSON.parse(localStorage.getItem("user"));

  useEffect(() => {
    const type = routeType();
    const isLogged = isLoggedIn();
    switch (type) {
      case "non-auth":
        if (isLogged) history.push("/new-order/single");
        break;
      case "auth":
        if (!isLogged) {
          localStorage.removeItem("bulk");
          history.push("/login");
        }
        break;
      default:
        break;
    }
  }, [pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  const routeType = () => {
    switch (pathname.split("/")[1]) {
      case "login":
      case "signup":
      case "verify-code":
        return "non-auth";
      case "tracking":
      case "feedback":
      case "privacy-policy":
        return "allowed";
      default:
        return "auth";
    }
  };

  const SuspenseFallback = () => {
    return (
      <div className='loading-wrapper d-flex align-items-center justify-content-center'>
        <Loading />
      </div>
    );
  };

  return (
    <Switch>
      <Suspense fallback={<SuspenseFallback />}>
        <Switch>
          <Redirect exact from='/' to='/login' />

          <Route exact path={`/privacy-policy`}>
            <PrivacyPolicyFa />
          </Route>

          <Route exact path={`/tracking/:id`}>
            <ReceiverTracking />
          </Route>

          <Route exact path={`/feedback/:id`}>
            <ReceiverFeedback />
          </Route>
          
          <Route exact path={`/feedback/sender/:shopId/:orderId`}>
            <SenderFeedback />
          </Route>

          <Route exact path={["/login", "/signup", "/verify-code"]}>
            <AuthContainer>
              <Suspense fallback={<SuspenseFallback />}>
                <Route exact path={`/login`}>
                  <Login />
                </Route>

                <Route exact path={`/signup`}>
                  <Register />
                </Route>

                <Route exact path={`/verify-code`}>
                  <VerifyCode />
                </Route>
              </Suspense>
            </AuthContainer>
          </Route>

          <Route
            exact
            path={[
              "/profile",
              "/profile/info",
              "/profile/address-book",
              "/profile/setting",
              "/dashboard",
              "/file-list",
              "/new-order",
              "/new-order/single",
              "/new-order/bulk",
              "/new-order/preview",
              "/cart",
              "/shipping",
              "/shipping/details/:id",
              "/payment",
              "/finalize",
              "/orders",
              "/order/:id",
            ]}
          >
            <PageView>
              <Suspense fallback={<SuspenseFallback />}>
                <CityDataProvider>
                  <Route exact path={`/profile`}>
                    <Profile />
                  </Route>

                  <Route exact path={`/profile/info`}>
                    <ProfileInfo />
                  </Route>

                  <Route exact path={`/profile/address-book`}>
                    <AddressBook />
                  </Route>

                  <Route exact path={`/dashboard`}>
                    <Dashboard />
                  </Route>

                  <Route exact path={`/file-list`}>
                    <FileList />
                  </Route>

                  <OrderDataProvider>
                    <BoxSizeDataProvider>
                      <Route path={["/new-order"]}>
                        <NewOrderContainer>
                          <Suspense fallback={<SuspenseFallback />}>
                            <Route exact path={`/new-order/single`}>
                              <SingleOrder />
                            </Route>
                            <BulkDataProvider>
                              <Route exact path={[`/new-order/bulk`, `/new-order/preview`]}>
                                <BulkOrder />
                              </Route>
                            </BulkDataProvider>
                          </Suspense>
                        </NewOrderContainer>
                      </Route>

                      <Route exact path={`/cart`}>
                        <Cart />
                      </Route>

                      <Route exact path={`/shipping`}>
                        <Shipping />
                      </Route>

                      <Route exact path={`/shipping/details/:id`}>
                        <ShippingInfo />
                      </Route>

                      <Route exact path={`/payment`}>
                        <Payment />
                      </Route>

                      <Route exact path={`/finalize`}>
                        <Finalized />
                      </Route>
                    </BoxSizeDataProvider>
                  </OrderDataProvider>

                  <OrderFiltersDataProvider>
                    <Route exact path={`/orders`}>
                      <OrdersList />
                    </Route>

                    <Route exact path={`/order/:id`}>
                      <OrderDetails />
                    </Route>
                  </OrderFiltersDataProvider>
                </CityDataProvider>
              </Suspense>
            </PageView>
          </Route>

          <Route>
            <NotFound />
          </Route>
        </Switch>
      </Suspense>
    </Switch>
  );
};

export default Router;
